// frontend/src/components/reviews/ReviewSection.js
import React, { useState, useEffect, useCallback } from 'react';
import { reviewsApi } from '../../api/reviews';
import { useAuth } from '../../context/AuthContext';
import StarRating from '../common/StarRating';
import { FaUserCircle, FaSpinner, FaCheckCircle, FaLock } from 'react-icons/fa';

function ReviewSection({ partId }) {
  const { isAuthenticated } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form State
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchReviews = useCallback(async () => {
    try {
      const data = await reviewsApi.getPartReviews(partId);
      setReviews(data || []);
    } catch (err) {
      console.error("Failed to load reviews", err);
    } finally {
      setLoading(false);
    }
  }, [partId]);

  useEffect(() => {
    if (partId) {
        fetchReviews();
    }
  }, [partId, fetchReviews]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) return alert("Please select a rating.");
    
    setSubmitting(true);
    setMessage(null);
    
    try {
      await reviewsApi.submitReview({ part_id: partId, rating, comment });
      setMessage({ type: 'success', text: 'Review submitted! It will appear after approval.' });
      setRating(0);
      setComment('');
    } catch (err) {
      // Handle the specific Verified Purchase error
      const errorMsg = err.message || 'Failed to submit review.';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-12 border-t border-gray-200 pt-8 pb-10">
      <h3 className="text-2xl font-bold text-gray-800 mb-6">Customer Reviews</h3>

      {/* Reviews List */}
      <div className="space-y-6 mb-10">
        {loading ? (
          <div className="text-gray-500 flex items-center gap-2"><FaSpinner className="animate-spin"/> Loading reviews...</div>
        ) : reviews.length === 0 ? (
          <div className="text-gray-500 italic bg-gray-50 p-4 rounded border border-gray-100">
              No reviews yet. Be the first to review this part!
          </div>
        ) : (
          reviews.map(review => (
            <div key={review.id} className="bg-gray-50 p-4 rounded-lg border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <FaUserCircle className="text-gray-400 text-xl" />
                  <span className="font-bold text-gray-800">{review.user_name}</span>
                  {/* Verified Badge */}
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1 font-medium">
                      <FaCheckCircle size={10} /> Verified Purchase
                  </span>
                </div>
                <span className="text-xs text-gray-500">{new Date(review.created_at).toLocaleDateString()}</span>
              </div>
              <div className="mb-2">
                <StarRating rating={review.rating} readOnly size={14} />
              </div>
              <p className="text-gray-700 text-sm">{review.comment}</p>
            </div>
          ))
        )}
      </div>

      {/* Write Review Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h4 className="font-bold text-gray-800 mb-4 text-lg">Write a Review</h4>
        
        {!isAuthenticated ? (
          <p className="text-sm text-gray-600">
            Please <a href="/login" className="text-primary hover:underline font-bold">log in</a> to write a review.
          </p>
        ) : (
          <form onSubmit={handleSubmit}>
            {message && (
              <div className={`p-3 rounded mb-4 text-sm flex items-start gap-2 ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {message.type === 'error' && <FaLock className="mt-1" />}
                {message.text}
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
              <StarRating rating={rating} setRating={setRating} />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Comment</label>
              <textarea 
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                required
                rows="3"
                className="w-full p-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none"
                placeholder="Share your experience with this part..."
              ></textarea>
            </div>
            
            <button 
              type="submit" 
              disabled={submitting}
              className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-6 rounded transition duration-300 flex items-center gap-2"
            >
              {submitting ? <FaSpinner className="animate-spin" /> : 'Submit Review'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ReviewSection;